import type { IIntegrationDefinition } from "../constants/integration";
import { COMPOSIO_INTEGRATION_DEFINITIONS } from "../constants/integration";
import type { IIntegrationConnectPrompt } from "../types/chat";
import { EIntegrationConnectResolution } from "../types/chat";
import type { IConnectIntegrationPayload } from "../types/integration";
import { EConnectorType, EIntegrationProvider } from "../types/integration";
import {
  EIntegrationConnectActionKind,
  EIntegrationConnectStatusKey,
  type IBuildConnectPayloadForDefinitionProps,
  type ICanSupersedeIntegrationConnectResolutionProps,
  type IIsIntegrationConnectPromptActionableProps,
  type IResolveIntegrationConnectActionProps,
  type IResolveIntegrationConnectStatusKeyProps,
  type TIntegrationConnectAction,
} from "./integration-connect-prompt.types";
import { getAvailableConnectors } from "./integration-connector";

// NOTE: NSAI-5681 — helpers behind the inline "Connect <app>" card that the
// server's requestIntegrationConnection tool raises under an assistant reply.
// Shared so web and mobile make the same routing decision the Integrations
// page makes (see web/src/hooks/use-integration.ts handleConnect), instead of
// each client re-deriving it.

const INTEGRATION_PROVIDER_VALUES = new Set<string>(
  Object.values(EIntegrationProvider),
);

export const isIntegrationProvider = (
  provider: string,
): provider is EIntegrationProvider =>
  INTEGRATION_PROVIDER_VALUES.has(provider);

// NOTE: One card per app is the whole identity — the card offers the provider's
// own connect flow and names no account, so the same provider is the same card
// however many times the event is replayed or the history refetched.
// NOTE: Which record a new one may replace, mirroring the server rule of the
// same name (chat/utils/integration-connect-resolution.utils):
//
//   open       — anything may resolve it
//   failed     — the only non-final outcome: a retry can still connect, and the
//                next message still terminates it
//   terminated — inert, EXCEPT for a connect that actually landed (click
//                Connect, send another message, then finish the OAuth flow)
//   connected
//   dismissed  — final, so a replayed frame can never rewrite history
export const canSupersedeIntegrationConnectResolution = ({
  current,
  next,
}: ICanSupersedeIntegrationConnectResolutionProps): boolean => {
  if (
    current === undefined ||
    current === EIntegrationConnectResolution.FAILED
  ) {
    return true;
  }

  return (
    current === EIntegrationConnectResolution.TERMINATED &&
    next === EIntegrationConnectResolution.CONNECTED
  );
};

// NOTE: NSAI-8152 — an MCP card that names a server adds that server's URL to
// the identity, mirroring the server's dedupe: "connect Notion and Linear MCP"
// is two cards, and a resolved Notion record must not replace the Linear card.
export const getIntegrationConnectPromptKey = (
  prompt: IIntegrationConnectPrompt,
): string =>
  prompt.mcpServer
    ? `${prompt.provider}::${prompt.mcpServer.url}`
    : prompt.provider;

export interface IMergeIntegrationConnectPromptsProps {
  existing: IIntegrationConnectPrompt[] | undefined;
  incoming: IIntegrationConnectPrompt[];
}

// NOTE: Live event and history read return the same records for the same
// message, so a replayed socket frame or a refetch landing after the live one
// must not stack a twin card. First-seen order is kept — the server appends.
//
// The exception is a record that supersedes what is on screen. The server
// resolves a card when the OAuth callback lands — the only place that knows
// whether the connect actually succeeded — and pushes the resolved record to
// the room. An OPEN card is replaced by any outcome, and a FAILED one by a
// later outcome too: retrying after a failure ends in CONNECTED, and the next
// message ends it as TERMINATED. Mirrors the server's own supersede rule
// (canSupersedeIntegrationConnectResolution in chat/utils), so a pushed record
// and a refetched one agree.
export const mergeIntegrationConnectPrompts = ({
  existing,
  incoming,
}: IMergeIntegrationConnectPromptsProps): IIntegrationConnectPrompt[] => {
  const merged = new Map<string, IIntegrationConnectPrompt>();
  for (const prompt of [...(existing ?? []), ...incoming]) {
    const key = getIntegrationConnectPromptKey(prompt);
    const seen = merged.get(key);
    // NOTE: The INCOMING record must itself be an outcome to displace what is
    // already held — testing only the held record would let a replayed OPEN
    // frame erase a FAILED card, and would let a second open frame overwrite
    // the first-seen one (losing its createdAt, which orders the card stack).
    const canReplace =
      prompt.resolution !== undefined &&
      canSupersedeIntegrationConnectResolution({
        current: seen?.resolution,
        next: prompt.resolution,
      });

    if (!seen || canReplace) {
      merged.set(key, prompt);
    }
  }
  return Array.from(merged.values());
};

const MAP_RESOLUTION_TO_STATUS_KEY: Record<
  EIntegrationConnectResolution,
  EIntegrationConnectStatusKey
> = {
  [EIntegrationConnectResolution.CONNECTED]:
    EIntegrationConnectStatusKey.CONNECTED,
  [EIntegrationConnectResolution.FAILED]: EIntegrationConnectStatusKey.FAILED,
  [EIntegrationConnectResolution.DISMISSED]:
    EIntegrationConnectStatusKey.DISMISSED,
  [EIntegrationConnectResolution.TERMINATED]:
    EIntegrationConnectStatusKey.TERMINATED,
};

// NOTE: The card's status line. A RECORDED outcome always wins — it is the
// durable fact, written by the server when the OAuth callback landed, identical
// on both clients and after any reload. An unresolved card falls back to the
// missing-ACCOUNT wording for an app that was already connected when the card
// was raised.
export const resolveIntegrationConnectStatusKey = ({
  resolution,
  hasExistingConnection,
  isSuperseded = false,
}: IResolveIntegrationConnectStatusKeyProps): EIntegrationConnectStatusKey => {
  // NOTE: A newer user message means the server is stamping TERMINATED for this
  // same card as that message is persisted, so the card must already read as
  // terminated — otherwise the same card looks one way now and another way
  // after a refresh (reported: a FAILED card kept its orange "try again" copy
  // while the server had already turned it grey). Which resolutions the stamp
  // may overwrite is the SERVER's rule, asked here rather than restated:
  // an open card and a failed one become TERMINATED; connected and dismissed
  // are answers the next message does not undo.
  if (
    isSuperseded &&
    canSupersedeIntegrationConnectResolution({
      current: resolution,
      next: EIntegrationConnectResolution.TERMINATED,
    })
  ) {
    return EIntegrationConnectStatusKey.TERMINATED;
  }
  if (resolution) {
    return MAP_RESOLUTION_TO_STATUS_KEY[resolution];
  }
  return hasExistingConnection
    ? EIntegrationConnectStatusKey.DESCRIPTION_ANOTHER
    : EIntegrationConnectStatusKey.DESCRIPTION;
};

// NOTE: Which cards still offer their buttons. An open card does, and a FAILED
// one keeps them because a failed OAuth is usually transient and retyping the
// whole request to get a new card would be a poor trade. Everything else is a
// record: connected and dismissed are answered, and TERMINATED is what the
// next message turns a still-open (or failed) card into.
export const isIntegrationConnectPromptActionable = ({
  resolution,
  isSuperseded = false,
}: IIsIntegrationConnectPromptActionableProps): boolean =>
  !isSuperseded &&
  (resolution === undefined ||
    resolution === EIntegrationConnectResolution.FAILED);

// NOTE: Last-resort display name when the provider has no catalog entry on
// this client (a slug enabled server-side that the definitions do not know
// yet): "azure-microsoft-teams" → "Azure Microsoft Teams". Callers prefer the
// definition's i18n nameKey and only fall through to this.
export const formatIntegrationProviderFallbackLabel = (
  provider: string,
): string =>
  provider
    .split(/[-_]/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const findIntegrationDefinition = (
  provider: string,
): IIntegrationDefinition | undefined =>
  COMPOSIO_INTEGRATION_DEFINITIONS.find(
    (definition) =>
      definition.provider === provider || definition.id === provider,
  );

// NOTE: Mirrors `startConnect` on the Integrations page: the external-provider
// flag rides along unless the definition opts out, the legacy
// `integrationType` is set for the providers that still expect it, and the
// connector rail is pinned only when the caller chose one.
export const buildConnectPayloadForDefinition = ({
  definition,
  isExternalIntegrationsProviderEnabled,
  connectorType,
}: IBuildConnectPayloadForDefinitionProps): Omit<
  IConnectIntegrationPayload,
  "redirectTo"
> | null => {
  const provider = definition.provider;
  if (!provider) {
    return null;
  }

  return {
    provider,
    ...(definition.skipExternalProviderCheck
      ? {}
      : { isExternalIntegrationsProviderEnabled }),
    ...(definition.integrationType
      ? { integrationType: definition.integrationType }
      : {}),
    ...(connectorType ? { connectorType } : {}),
  };
};

// NOTE: Decides what the card's Connect button does for a provider, in the
// same order the Integrations page decides it:
//   1. Not enabled for this client / hidden for this org → the page (it shows
//      the truth; the server already validated the provider, so this is a
//      stale-cache or feature-flag edge, not the common path).
//   2. Several connector rails → let the user pick one.
//   3. Only the Pipedream rail → connect through it without a form.
//   4. A credential form is needed → open that provider's own form; connect
//      directly only where the external provider takes over the form.
//   5. Plain OAuth provider → connect right here.
export const resolveIntegrationConnectAction = ({
  provider,
  providerInfo,
  isExternalIntegrationsProviderEnabled,
  definition = findIntegrationDefinition(provider),
}: IResolveIntegrationConnectActionProps): TIntegrationConnectAction => {
  if (
    !isIntegrationProvider(provider) ||
    !providerInfo ||
    providerInfo.isOrgConfigured === false ||
    !definition ||
    !definition.provider
  ) {
    return { kind: EIntegrationConnectActionKind.OPEN_INTEGRATIONS };
  }

  if (providerInfo.showConnectorPicker) {
    return {
      kind: EIntegrationConnectActionKind.PICK_CONNECTOR,
      provider,
      availableConnectors: getAvailableConnectors(providerInfo),
    };
  }

  if (
    providerInfo.hasNodeshiftConnector === false &&
    providerInfo.hasPipedreamConnector
  ) {
    const payload = buildConnectPayloadForDefinition({
      definition,
      isExternalIntegrationsProviderEnabled,
      connectorType: EConnectorType.PIPEDREAM,
    });
    return payload
      ? { kind: EIntegrationConnectActionKind.CONNECT, payload }
      : { kind: EIntegrationConnectActionKind.OPEN_INTEGRATIONS };
  }

  if (definition.modalKey) {
    if (
      definition.fallbackToExternalProvider &&
      isExternalIntegrationsProviderEnabled
    ) {
      return {
        kind: EIntegrationConnectActionKind.CONNECT,
        payload: { provider, isExternalIntegrationsProviderEnabled },
      };
    }
    return {
      kind: EIntegrationConnectActionKind.OPEN_MODAL,
      modalKey: definition.modalKey,
    };
  }

  const payload = buildConnectPayloadForDefinition({
    definition,
    isExternalIntegrationsProviderEnabled,
  });
  return payload
    ? { kind: EIntegrationConnectActionKind.CONNECT, payload }
    : { kind: EIntegrationConnectActionKind.OPEN_INTEGRATIONS };
};
